/**
 * @fileoverview TourFeatureCard.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import DynamicImage from '../ui/DynamicImage'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

const getIcon = (feature) => {
  switch (feature.toLowerCase()) {
    case 'car': 
    case 'transport': return 'fi fi-br-car-side';
    case 'hotel': return 'fi fi-br-hotel';
    case 'meal': return 'fi fi-br-restaurant';
    case 'guide': return 'fi fi-br-users';
    case 'hiking': return 'fi fi-br-hiking';
    case 'bicycle': return 'fi fi-br-bicycle';
    case 'bed': return 'fi fi-br-bed';
    default: return 'fi fi-br-check';
  }
}

function TourFeatureCard({ tour, index = 0 }) {
  if (!tour) return null;

  return (
    <motion.div
      className='flex flex-col justify-between items-center bg-white w-full max-w-sm mx-auto md:mx-0 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden border border-gray-100 group'
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: index * 0.1 }}
    >
      <Link to={`/tours/${tour.slug || tour.id}`} className='w-full aspect-[4/3] relative overflow-hidden'>
        <DynamicImage 
          LowImage={tour.image.l} 
          HighImage={tour.image.h} 
          alt={`Sri Lanka Tour: ${tour.name} - ${tour.category}`}
        />
      </Link>
      
      <div className='w-full p-6 flex flex-col gap-4 text-left'>
        <div className='flex justify-between items-start'>
            <div className='flex flex-col gap-1'>
                <h3 className='text-lg md:text-xl font-bold text-gray-800 hover:text-KITH-primary transition-colors duration-200 leading-tight'>
                    <Link to={`/tours/${tour.slug || tour.id}`}>
                        {tour.name}
                    </Link>
                </h3>
                <p className='text-[10px] md:text-xs text-KITH-primary-dark font-bold uppercase tracking-wide'>{tour.duration}</p>
            </div>
        </div>
        
        <p className='text-sm text-gray-600 leading-relaxed text-justify line-clamp-3'>
          {tour.description}
        </p>

        <div className='flex justify-between items-center mt-3'>
          <div className='flex gap-3'>
            {tour.featuredIcons && tour.featuredIcons.map((feature, idx) => (
                <i key={idx} className={`${getIcon(feature)} text-KITH-primary text-xl`} title={feature}></i>
            ))}
          </div>

          <div className='text-right'>
            <p className='text-[10px] text-gray-400 font-medium'>From</p>
            <p className='text-lg font-bold text-gray-800'>USD {tour.price.toLocaleString()}.00 <span className='text-[10px] font-medium text-gray-500'>/pp</span></p>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default TourFeatureCard
